import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Check,
  Sparkles,
  Shield,
  Loader2,
  AlertCircle,
  Calendar,
  Crown,
  Zap,
  Star,
  Gift,
  Clock,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useConfirm } from "../hooks/useConfirm";

const PLAN_ICONS = {
  starter: Zap,
  pro: Star,
  enterprise: Crown,
};

/** Dias inteiros restantes até `iso` (pode ser negativo se já passou). */
function daysUntil(iso) {
  if (!iso) return null;
  const end = new Date(iso);
  if (isNaN(end.getTime())) return null;
  const now = new Date();
  return Math.ceil((end - now) / (1000 * 60 * 60 * 24));
}

export default function Billing() {
  const [plans, setPlans] = useState([]);
  const [state, setState] = useState(null);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [redirecting, setRedirecting] = useState(false);
  const confirm = useConfirm();
  const nav = useNavigate();

  async function load() {
    setLoading(true);
    try {
      const [p, s, u] = await Promise.all([
        api.get("/billing/plans"),
        api.get("/billing/plan"),
        api.get("/billing/usage").catch(() => ({ data: null })),
      ]);
      setPlans(p.data || []);
      setState(s.data || null);
      setUsage(u.data || null);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Falha ao carregar");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  async function subscribe(plan) {
    setRedirecting(true);
    try {
      const { data } = await api.post("/billing/checkout", {
        plan_id: plan.id,
        origin_url: window.location.origin,
      });
      if (data?.url) {
        window.location.href = data.url;
      } else {
        toast.error("Falha ao iniciar checkout");
        setRedirecting(false);
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Falha no checkout");
      setRedirecting(false);
    }
  }

  async function cancelPlan() {
    const ok = await confirm({
      title: "Cancelar assinatura?",
      description:
        "Seu plano voltará para 'Free' e você perderá acesso a recursos premium. Os dados da empresa permanecem intactos.",
      destructive: true,
      actionLabel: "Cancelar plano",
    });
    if (!ok) return;
    try {
      await api.post("/billing/cancel");
      toast.success("Assinatura cancelada");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Falha");
    }
  }

  if (loading) {
    return (
      <div className="p-10 text-center text-slate-500">
        <Loader2 size={20} className="animate-spin inline mr-2" /> Carregando…
      </div>
    );
  }

  const currentPlanId = state?.plan_id || "free";
  const isOwner = currentPlanId === "owner";

  // Está em teste grátis se há data de fim de trial e nunca houve pagamento.
  const onTrial = !!state?.trial_ends_at && !state?.last_payment_at && !isOwner;
  const trialDays = onTrial ? daysUntil(state.trial_ends_at) : null;

  return (
    <div className="p-4 sm:p-6 space-y-8 max-w-7xl mx-auto" data-testid="billing-page">
      <header className="text-center space-y-2">
        <h1 className="text-3xl sm:text-4xl font-bold text-slate-900">
          Plano & Cobrança
        </h1>
        <p className="text-slate-600">
          Escolha o plano que melhor atende sua empresa. Cancele quando quiser.
        </p>
      </header>

      {/* Aviso de trial (contagem regressiva + alerta amigável) */}
      {onTrial && <TrialBanner days={trialDays} planName={state?.plan?.name} endsAt={state.trial_ends_at} />}

      {/* Current state */}
      <CurrentStateCard state={state} isOwner={isOwner} onCancel={cancelPlan} onTrial={onTrial} trialDays={trialDays} />

      {/* Usage */}
      {usage && <UsageCard usage={usage} />}

      {/* Plans grid */}
      <div className="grid md:grid-cols-3 gap-5">
        {plans.map((p) => (
          <PlanCard
            key={p.id}
            plan={p}
            current={currentPlanId === p.id}
            disabled={isOwner || redirecting}
            onSubscribe={() => subscribe(p)}
          />
        ))}
      </div>

      <div className="text-center text-xs text-slate-500 space-y-1">
        <p className="flex items-center justify-center gap-1.5">
          <Shield size={12} /> Pagamentos processados pelo Stripe. Não armazenamos
          seu cartão.
        </p>
        <p>
          Renovação automática a cada 30 dias. Cancele a qualquer momento e
          continue até o fim do ciclo.
        </p>
      </div>
    </div>
  );
}

function UsageCard({ usage }) {
  const items = [
    { key: "collaborators", label: "Colaboradores", icon: "users" },
    { key: "clients", label: "Clientes", icon: "briefcase" },
    { key: "teams", label: "Equipes", icon: "team" },
  ];
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-5">
      <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-4">
        📊 Uso atual do seu plano
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {items.map((it) => {
          const used = usage.usage?.[it.key] ?? 0;
          const limit = usage.limits?.[`max_${it.key}`] ?? 0;
          const unlimited = limit === 0;
          const pct = unlimited ? 0 : Math.min(100, (used / limit) * 100);
          const overOrAt = !unlimited && used >= limit;
          return (
            <div key={it.key} className="space-y-2">
              <div className="flex items-baseline justify-between">
                <span className="font-semibold text-slate-900">{it.label}</span>
                <span
                  className={`text-xs font-mono ${
                    overOrAt
                      ? "text-red-600"
                      : pct >= 80
                        ? "text-amber-600"
                        : "text-slate-500"
                  }`}
                >
                  {used} / {unlimited ? "∞" : limit}
                </span>
              </div>
              <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                <div
                  className={`h-full transition-all ${
                    unlimited
                      ? "bg-emerald-500 w-full"
                      : overOrAt
                        ? "bg-red-500"
                        : pct >= 80
                          ? "bg-amber-500"
                          : "bg-emerald-500"
                  }`}
                  style={{ width: unlimited ? "100%" : `${pct}%` }}
                />
              </div>
              {overOrAt && (
                <div className="text-[11px] text-red-600">
                  Limite atingido. Faça upgrade para adicionar mais.
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function TrialBanner({ days, planName, endsAt }) {
  const endStr = endsAt ? new Date(endsAt).toLocaleDateString("en-US") : null;
  const expired = typeof days === "number" && days < 0;
  const urgent = typeof days === "number" && days >= 0 && days <= 3;

  const tone = expired
    ? {
        wrap: "bg-red-50 border-red-200",
        icon: "bg-red-100 text-red-600",
        title: "text-red-800",
        text: "text-red-700",
      }
    : urgent
      ? {
          wrap: "bg-amber-50 border-amber-200",
          icon: "bg-amber-100 text-amber-700",
          title: "text-amber-800",
          text: "text-amber-700",
        }
      : {
          wrap: "bg-blue-50 border-blue-200",
          icon: "bg-blue-100 text-blue-700",
          title: "text-blue-800",
          text: "text-blue-700",
        };

  let title;
  let msg;
  if (expired) {
    title = "Seu teste grátis terminou";
    msg = "Assine um plano abaixo para reativar o acesso completo à sua conta.";
  } else if (days === 0) {
    title = "Seu teste grátis termina hoje";
    msg = `Assine ${planName ? `o plano ${planName}` : "um plano"} abaixo para não perder o acesso.`;
  } else if (urgent) {
    title = `Faltam ${days} dia${days === 1 ? "" : "s"} de teste grátis`;
    msg = `Seu período de avaliação encerra em ${endStr}. Assine para continuar sem interrupções.`;
  } else {
    title = `Você está no teste grátis — faltam ${days} dias`;
    msg = `Aproveite todos os recursos${planName ? ` do plano ${planName}` : ""} até ${endStr}. Você paga só quando o teste acabar.`;
  }

  return (
    <div
      data-testid="trial-banner"
      className={`rounded-2xl border p-4 sm:p-5 flex items-start gap-3 ${tone.wrap}`}
    >
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${tone.icon}`}>
        {expired ? <AlertCircle size={20} /> : urgent ? <Clock size={20} /> : <Gift size={20} />}
      </div>
      <div className="min-w-0">
        <div className={`font-bold ${tone.title}`} data-testid="trial-banner-title">
          {title}
        </div>
        <div className={`text-sm mt-0.5 ${tone.text}`}>{msg}</div>
      </div>
    </div>
  );
}

function CurrentStateCard({ state, isOwner, onCancel, onTrial, trialDays }) {
  if (!state) return null;
  const paid = state.plan_paid_until
    ? new Date(state.plan_paid_until).toLocaleDateString("en-US")
    : null;
  const isPaid = ["starter", "pro", "enterprise"].includes(state.plan_id);
  const trialExpired = typeof trialDays === "number" && trialDays < 0;
  return (
    <div className="bg-gradient-to-br from-slate-50 to-white border border-slate-200 rounded-2xl p-5">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">
            Plano atual
          </div>
          <div className="text-2xl font-bold text-slate-900 mt-1 flex items-center gap-2 flex-wrap">
            {state.plan?.name || "Free"}
            {isOwner && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-violet-100 text-violet-700 border border-violet-200 font-semibold">
                <Crown size={11} className="inline mr-1" /> Owner
              </span>
            )}
            {onTrial && !trialExpired && (
              <span
                data-testid="trial-badge"
                className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 border border-blue-200 font-semibold"
              >
                <Gift size={11} className="inline mr-1" />
                Teste grátis · {trialDays} dia{trialDays === 1 ? "" : "s"} restante{trialDays === 1 ? "" : "s"}
              </span>
            )}
            {onTrial && trialExpired && (
              <span
                data-testid="trial-badge"
                className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-700 border border-red-200 font-semibold"
              >
                <Clock size={11} className="inline mr-1" /> Teste expirado
              </span>
            )}
            {state.status === "suspended" && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-700 border border-red-200 font-semibold">
                <AlertCircle size={11} className="inline mr-1" /> Suspenso
              </span>
            )}
          </div>
          {/* Durante o trial mostramos o fim do teste; após pagar, a renovação. */}
          {onTrial ? (
            <div className="text-sm text-blue-700 mt-1.5 flex items-center gap-1.5" data-testid="trial-ends-line">
              <Sparkles size={13} /> Teste grátis até{" "}
              {new Date(state.trial_ends_at).toLocaleDateString("en-US")}
            </div>
          ) : (
            paid && (
              <div className="text-sm text-slate-600 mt-1.5 flex items-center gap-1.5">
                <Calendar size={13} /> Renovação até {paid}
              </div>
            )
          )}
        </div>
        {isPaid && !isOwner && !onTrial && (
          <button
            onClick={onCancel}
            data-testid="cancel-plan-btn"
            className="text-sm font-semibold px-3 py-2 rounded-lg border border-red-200 bg-red-50 text-red-700 hover:bg-red-100 transition"
          >
            Cancelar plano
          </button>
        )}
      </div>
    </div>
  );
}

function PlanCard({ plan, current, disabled, onSubscribe }) {
  const Icon = PLAN_ICONS[plan.id] || Sparkles;
  const popular = plan.popular;
  return (
    <div
      data-testid={`plan-card-${plan.id}`}
      className={`relative rounded-2xl border bg-white p-6 flex flex-col gap-4 transition-all ${
        popular
          ? "border-blue-500 shadow-xl scale-[1.02]"
          : current
            ? "border-emerald-400 shadow-md"
            : "border-slate-200 hover:border-slate-300"
      }`}
    >
      {popular && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-xs font-bold bg-blue-600 text-white shadow-md">
          MAIS POPULAR
        </div>
      )}
      {current && (
        <div className="absolute -top-3 right-4 px-3 py-1 rounded-full text-xs font-bold bg-emerald-600 text-white shadow-md">
          ATIVO
        </div>
      )}
      <div className="flex items-center gap-2">
        <div
          className={`w-10 h-10 rounded-lg flex items-center justify-center ${
            popular ? "bg-blue-100 text-blue-700" : "bg-slate-100 text-slate-700"
          }`}
        >
          <Icon size={20} />
        </div>
        <div className="text-xl font-bold text-slate-900">{plan.name}</div>
      </div>
      <div>
        <span className="text-4xl font-bold text-slate-900">
          ${plan.price_usd.toFixed(0)}
        </span>
        <span className="text-slate-500 ml-1">/mês</span>
      </div>
      <ul className="space-y-2 flex-1">
        {plan.features.map((f) => (
          <li key={`${plan.key}-feat-${f}`} className="flex items-start gap-2 text-sm text-slate-700">
            <Check size={16} className="text-emerald-500 flex-shrink-0 mt-0.5" />
            <span>{f}</span>
          </li>
        ))}
      </ul>
      <button
        onClick={onSubscribe}
        disabled={disabled || current}
        data-testid={`subscribe-${plan.id}`}
        className={`w-full py-3 rounded-xl font-semibold text-sm transition ${
          current
            ? "bg-emerald-100 text-emerald-700 cursor-default"
            : popular
              ? "bg-blue-600 text-white hover:bg-blue-700 disabled:bg-blue-300"
              : "bg-slate-900 text-white hover:bg-slate-800 disabled:bg-slate-400"
        }`}
      >
        {current ? "Plano atual" : `Assinar ${plan.name}`}
      </button>
    </div>
  );
}

/**
 * Post-checkout success page — polls /billing/checkout/{id} until paid.
 */
export function BillingSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("polling");
  const [info, setInfo] = useState(null);
  const nav = useNavigate();

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }
    let attempts = 0;
    const max = 10;
    const interval = setInterval(async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/billing/checkout/${sessionId}`);
        setInfo(data);
        if (data.payment_status === "paid") {
          setStatus("paid");
          clearInterval(interval);
        } else if (data.status === "expired" || attempts >= max) {
          setStatus(data.status === "expired" ? "expired" : "timeout");
          clearInterval(interval);
        }
      } catch (e) {
        if (attempts >= max) {
          setStatus("error");
          clearInterval(interval);
        }
      }
    }, 2000);
    return () => clearInterval(interval);
  }, [sessionId]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-blue-50 p-4">
      <div className="bg-white border border-slate-200 rounded-2xl shadow-xl p-8 max-w-md w-full text-center space-y-4">
        {status === "polling" && (
          <>
            <Loader2 size={48} className="mx-auto text-blue-500 animate-spin" />
            <h1 className="text-2xl font-bold text-slate-900">Confirmando pagamento…</h1>
            <p className="text-sm text-slate-600">
              Estamos validando com o Stripe. Em alguns segundos seu plano será
              ativado.
            </p>
          </>
        )}
        {status === "paid" && (
          <>
            <div className="w-16 h-16 rounded-full bg-emerald-100 text-emerald-600 mx-auto flex items-center justify-center">
              <Check size={32} strokeWidth={3} />
            </div>
            <h1 className="text-2xl font-bold text-slate-900">Pagamento confirmado!</h1>
            <p className="text-sm text-slate-600">
              Seu plano <strong>{info?.plan_id?.toUpperCase()}</strong> foi ativado.
              Bora trabalhar! 🚀
            </p>
            <button
              onClick={() => nav("/dashboard")}
              className="w-full py-3 rounded-xl bg-blue-600 text-white font-semibold hover:bg-blue-700"
              data-testid="success-continue"
            >
              Ir para o dashboard
            </button>
          </>
        )}
        {(status === "expired" || status === "timeout" || status === "error") && (
          <>
            <AlertCircle size={48} className="mx-auto text-red-500" />
            <h1 className="text-2xl font-bold text-slate-900">
              {status === "expired" ? "Sessão expirada" : "Algo deu errado"}
            </h1>
            <p className="text-sm text-slate-600">
              {status === "timeout"
                ? "Não conseguimos confirmar em tempo. Verifique no painel de Cobrança em alguns minutos."
                : "Tente novamente ou entre em contato com o suporte."}
            </p>
            <button
              onClick={() => nav("/billing")}
              className="w-full py-3 rounded-xl bg-slate-900 text-white font-semibold hover:bg-slate-800"
            >
              Voltar para Cobrança
            </button>
          </>
        )}
      </div>
    </div>
  );
}
